import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { ArrowRight, Sparkles, Smile, Activity, Layers, Pill, ScanEye, Scissors, Bone, Heart, Baby, X, Stethoscope } from "lucide-react";

// Map specialty name → icon
const SPECIALTY_ICONS = {
  "التنظيف": Sparkles,
  "التقويم / التقويم الشفاف (انفزلاين)": Smile,
  "علاج الجذور والأعصاب": Activity,
  "التركيبات والتلبيسات / العدسات": Layers,
  "الحشوات العلاجية والتجميلية": Pill,
  "زراعة الأسنان": ScanEye,
  "خلع الأسنان / ضروس العقل": Scissors,
  "أمراض وجراحة اللثة": Heart,
  "جراحة الوجه والفكين": Bone,
  "علاج آلام ومشاكل مفصل وعضلات الوجه والفكين": Activity,
  "علاج أسنان الأطفال": Baby,
};

const SPECIALTY_NOTES = {
  "التقويم / التقويم الشفاف (انفزلاين)": "جميع دكاترة التقويم استشاريين",
  "علاج الجذور والأعصاب": "جميع دكاترة علاج العصب استشاريين",
};

// ============== Pricing Dialog ==============
const PricingDialog = ({ doctor, onClose }) => {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doctor) return;
    setLoading(true);
    axios.get(`${API}/doctor-pricing/${encodeURIComponent(doctor.name)}`)
      .then(res => setServices(res.data.services || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [doctor]);

  if (!doctor) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      dir="rtl"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="doctor-pricing-dialog"
      >
        <div className="relative px-5 py-4 bg-gradient-to-l from-[#655dc6] to-[#7B70D4] text-white">
          <button
            onClick={onClose}
            className="absolute top-3 left-3 p-1.5 rounded-full hover:bg-white/20 transition-colors"
            data-testid="close-pricing-dialog"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 pr-8">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center font-bold text-lg shrink-0">
              {doctor.name.replace("د.", "").replace("بروفيسور", "").trim().charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base sm:text-lg leading-tight">{doctor.name}</h3>
              <p className="text-xs sm:text-sm text-white/85 mt-0.5">
                {doctor.role}{doctor.exp ? ` • خبرة ${doctor.exp}` : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-10 text-slate-400">جاري التحميل...</div>
          ) : services && services.length > 0 ? (
            <div className="space-y-2">
              {services.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#FAF9FE] border border-slate-100"
                >
                  <span className="text-slate-700 text-sm sm:text-base">{s.name}</span>
                  <span className="font-bold text-[#655dc6] text-sm sm:text-base whitespace-nowrap">
                    {s.price} ريال
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Pill className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">لا توجد أسعار مسجلة لهذا الطبيب حالياً</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== Components ==============
const DoctorCard = ({ doctor, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-white hover:bg-[#FAF9FE] border border-slate-100 hover:border-[#655dc6]/40 hover:shadow-md transition-all duration-200 text-right w-full cursor-pointer"
    data-testid={`doctor-${doctor.name}`}
  >
    <div className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-[#655dc6] to-[#8479d9] flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-sm">
      {doctor.name.replace("د.", "").replace("بروفيسور", "").trim().charAt(0)}
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="font-semibold text-slate-800 text-sm sm:text-base leading-tight">{doctor.name}</h4>
      <div className="flex flex-wrap items-center gap-1.5 mt-1">
        {doctor.role && <span className="text-xs text-[#655dc6] font-medium">{doctor.role}</span>}
        {doctor.exp && (
          <>
            <span className="text-slate-300 text-xs">•</span>
            <span className="text-xs text-slate-500">خبرة {doctor.exp}</span>
          </>
        )}
      </div>
    </div>
  </button>
);

const SpecialtySection = ({ specialty, docs, index, onDoctorClick }) => {
  const Icon = SPECIALTY_ICONS[specialty] || Stethoscope;
  const note = SPECIALTY_NOTES[specialty];
  return (
    <section
      className="rounded-2xl bg-white shadow-sm border border-slate-100 overflow-hidden"
      data-testid={`specialty-${index}`}
    >
      <div className="relative px-4 sm:px-5 py-3.5 bg-gradient-to-l from-[#655dc6]/5 via-[#655dc6]/10 to-[#655dc6]/5 border-b border-[#655dc6]/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#655dc6] flex items-center justify-center shadow-sm shrink-0">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-[#3d3680]">{specialty}</h3>
            {note && (
              <p className="text-xs sm:text-sm text-[#655dc6] font-medium mt-0.5">{note}</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {docs.map((doc) => (
          <DoctorCard key={doc.id} doctor={doc} onClick={() => onDoctorClick(doc)} />
        ))}
      </div>
    </section>
  );
};

// ============== Main Page ==============
const DoctorsPage = () => {
  const navigate = useNavigate();
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/doctors`)
      .then(res => {
        setSpecialties(res.data.specialties || []);
        setDoctors(res.data.doctors || []);
      })
      .catch(() => {
        setSpecialties([]);
        setDoctors([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const d of doctors) {
      if (!map[d.specialty]) map[d.specialty] = [];
      map[d.specialty].push(d);
    }
    return map;
  }, [doctors]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F7FE] via-white to-[#F8F7FE]" dir="rtl">
      <div className="relative bg-gradient-to-br from-[#655dc6] via-[#7B70D4] to-[#5449a8] overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors group"
              data-testid="doctors-back-btn"
            >
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              <span>رجوع</span>
            </button>
            <img src="/lucent-logo.png" alt="LUCENT" className="h-10 sm:h-12 brightness-0 invert" />
          </div>

          <div className="text-center text-white">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-xs sm:text-sm font-medium">شركة مركز لوسنت للأسنان</span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 tracking-tight">أطباء المركز</h1>
            <p className="text-sm sm:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
              نخبة من الاستشاريين والأخصائيين والأطباء العامين<br className="hidden sm:block" />
              المتخصصين في جميع فروع طب الأسنان
            </p>
            <p className="text-xs text-white/60 mt-4">اضغط على اسم الطبيب لعرض قائمة الأسعار</p>
          </div>
        </div>

        <svg className="block w-full" viewBox="0 0 1440 50" preserveAspectRatio="none">
          <path d="M0,20 C480,60 960,0 1440,30 L1440,50 L0,50 Z" fill="#F8F7FE" />
        </svg>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-4 sm:space-y-6 pb-12">
        {loading ? (
          <div className="text-center py-20 text-slate-400">جاري التحميل...</div>
        ) : (
          specialties
            .filter(sp => grouped[sp] && grouped[sp].length > 0)
            .map((sp, idx) => (
              <SpecialtySection
                key={sp}
                specialty={sp}
                docs={grouped[sp]}
                index={idx}
                onDoctorClick={setSelectedDoctor}
              />
            ))
        )}
      </div>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 text-center">
          <p className="text-xs sm:text-sm text-slate-500">
            © {new Date().getFullYear()} شركة مركز لوسنت للأسنان — Lucent Dental Center
          </p>
        </div>
      </footer>

      <PricingDialog doctor={selectedDoctor} onClose={() => setSelectedDoctor(null)} />
    </div>
  );
};

export default DoctorsPage;
